import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import type { Filter, SortField } from '@/models/common';
import type { RootResponse } from '@/models/rootResponse';
import type { SurveyHome } from '@/models/survey';
import services from '@/services';
import { useMainStore } from '@/store';
type SurveyApiResponse = RootResponse<SurveyHome[]>;
interface UseSurveysInfiniteQueryParams {
  filters: Filter[];
  search: string;
}
const SORT_FIELD_MAPPINGS = {
  status: 'status.label',
  creator: 'creator.lastname'
} as const;
export const useSurveysInfiniteQuery = ({
  filters,
  search
}: UseSurveysInfiniteQueryParams) => {
  const {
    currentUser
  } = useMainStore();
  const [sort, setSort] = useState<SortField[]>([]);
  const clientId = currentUser?.site?.client_id;
  const stableFilters = useMemo(() => JSON.stringify(filters), [filters]);
  const stableSort = useMemo(() => JSON.stringify(sort), [sort]);
  const queryKey = ['surveys-infinite', stableFilters, search, stableSort];
  const queryFn = useCallback(async (context: {
    pageParam: unknown;
  }): Promise<SurveyApiResponse> => {
    if (!currentUser?.id || !clientId) {
      throw new Error('User or client information is missing');
    }
    const page = context.pageParam as number || 1;
    return await services.survey.getSurveys({
      page,
      sorts: sort.map(sortField => ({
        field: sortField.field,
        direction: sortField.direction
      })),
      instructions: [{
        name: 'sorts-by-pinnable'
      }],
      filters: [...filters, {
        field: 'creator.site.client_id',
        operator: '=',
        value: clientId
      }],
      userId: currentUser.id,
      value: search,
      optimizedForCount: false
    });
  }, [sort, filters, search, clientId, currentUser?.id]);
  const getNextPageParam = useCallback((lastPage: SurveyApiResponse) => {
    const currentPage = lastPage.current_page ?? lastPage.meta?.current_page;
    const lastPageNum = lastPage.last_page ?? lastPage.meta?.last_page;
    if (currentPage && lastPageNum && currentPage < lastPageNum) {
      return currentPage + 1;
    }
    return undefined;
  }, []);
  const {
    data,
    isFetching,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isError,
    error
  } = useInfiniteQuery<SurveyApiResponse>({
    queryKey,
    queryFn,
    enabled: !!currentUser?.id && !!clientId,
    getNextPageParam,
    initialPageParam: 1,
    placeholderData: keepPreviousData,
    staleTime: 1 * 60 * 1000,
    refetchOnMount: true,
    networkMode: 'online',
    refetchOnReconnect: false,
    refetchIntervalInBackground: false
  });
  const surveys = useMemo(() => {
    return data?.pages?.flatMap(page_0 => page_0.data) ?? [];
  }, [data]);
  const total = useMemo(() => {
    if (!data?.pages?.length) return 0;
    const firstPage = data.pages[0];
    return firstPage.total ?? firstPage.meta?.total ?? 0;
  }, [data?.pages]);
  const transformSortField = useCallback((sortField_0: SortField): SortField => {
    const mappedField = SORT_FIELD_MAPPINGS[sortField_0.field as keyof typeof SORT_FIELD_MAPPINGS];
    return {
      ...sortField_0,
      field: mappedField || sortField_0.field
    };
  }, []);
  const handleSortChange = useCallback((newSort: SortField[]) => {
    if (!newSort.length) {
      setSort([]);
      return;
    }
    const transformedSort = newSort.map(transformSortField);
    setSort(transformedSort);
  }, [transformSortField]);
  return {
    surveys,
    isLoading,
    isError,
    error,
    isFetching,
    fetchNextPage,
    hasMoreData: hasNextPage,
    total,
    setSort,
    onSortChange: handleSortChange
  };
};